// This file builds columns for the analysis grid in the DataGrid component.
// created for reusability and maintainability in the compliance visualization epic.

import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { renderNumberCell } from "utils/dataGrid";

// This function formats the header name by replacing underscores with spaces
const formatHeaderName = (key: string) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// This function creates a column definition for issues in the analysis grid.
export const makeIssueColumn = (
  block: "problems" | "warnings",
  key: string,
  valueType: "total" | "reviewed" | "unreviewed",
  asPercentage: boolean
): GridColDef => ({
  field: `${block}_${key}_${valueType}`,
  headerName: formatHeaderName(key),
  width: 140,
  type: "number",
  filterable: false,
  sortable: key === "missing_coordinates",
  renderCell: asPercentage
    ? (params: GridRenderCellParams<any>) => {
        const value = params.value ?? 0;
        const totalVolume = params.row?.total_volume ?? 0;
        return totalVolume === 0
          ? "—"
          : `${((value / totalVolume) * 100).toFixed(1)}%`;
      }
    : renderNumberCell,
});

// Columns to hide by default in the analysis grid
export const legacyHiddenProblemColumns = [
  "missing_city",
  "outside_city",
  "outside_state",
  "wrong_country",
  "outside_country",
  "outside_shapefile",
  "unnamed_road",
  "address_with_url",
  "google_plus_code",
  "name_only_numbers",
  "nonstandard_state",
  "missing_postal_code",
  "name_equals_address",
  "address_only_numbers",
  "scrubbed_name_with_url",
  "name_with_potential_url",
  "scrubbed_name_very_long",
  "website_contains_google",
  "scrubbed_name_with_emoji",
  "name_equals_generic_value",
  "scrubbed_address_with_url",
  "address_with_potential_url",
  "scrubbed_address_with_emoji",
  "wrong_reverse_geocode_country",
  "expected_name_equals_address",
  "name_has_invalidating_string",
  "scrubbed_address_equals_neighborhood",
  "scrubbed_name_equals_scrubbed_address",
  "scrubbed_name_has_invalidating_string",
  "scrubbed_name_has_unicode_characters",
  "scrubbed_name_has_unusual_characters",
  "name_has_repeated_characters",
  "unexpected_name_equals_address",
  "unnamed_road_google_code_combo",
  "scrubbed_name_with_potential_url",
  "missing_reverse_geocode_country_code",
  "scrubbed_name_has_vertical_pipe_characters",
  "name_very_long",
  "name_very_short",
];

export const legacyHiddenWarningColumns = [
  "address_very_long",
  "address_very_short",
  "address_with_unicode_chars",
  "address_with_unusual_chars",
  "name_overlaps_address",
  "name_with_unicode_chars",
  "name_with_unusual_chars",
];
